import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { lmError, lmErrorCode } from './lmApi';
import { GhostButton, PrimaryButton } from './LmBits';
import { blockLabel } from './lmColumns';

/**
 * Bulk action lifecycle (#770 LM-02): confirm → progress → per-row outcomes →
 * retry only the failed rows.
 *
 * `run(selection, { reason, isAlive })` must resolve to the API's bulk envelope
 * `{operation_id, requested, succeeded, failed, results: [{row_id, ok, error}]}`.
 * List Management's bulk routes finish on the server in one request, so there
 * is no Cancel while it runs (a client-side abort would only stop waiting, not
 * the work). A `409 selection_changed` calls `onSelectionChanged` and closes:
 * retrying the same stale selector can never succeed.
 *
 * Also used outside List Management (Contacts' Contact Owner dialogs, #863):
 * `retryable(failedIds)` hides "Retry failed" when it returns false (an owner
 * conflict can never succeed against its pinned preview), and `requireReason`
 * keeps Confirm disabled until the `askReason` input holds text. Their `run`
 * starts a server-side operation and polls it, so with `background` the running
 * phase offers "Close — keep running in background": it closes the dialog and
 * turns `isAlive()` false, which stops the polling (the work goes on), then
 * calls `onBackground` (e.g. to refresh a list) instead of `onDone`. An error
 * flagged `stillRunning` (the poll gave up) is shown without "Try again".
 */
export default function LmBulkDialog({
    open, onClose, title, description, confirmLabel = 'Confirm', tone = 'accent',
    selection, count, run, askReason = false, reasonPlaceholder = 'Reason (shown in Deleted)',
    labelFor = (id) => id, onDone, extra, confirmDisabled = false, confirmHint, onSelectionChanged,
    requireReason = false, retryable = () => true, reasonMaxLength = 200, background = false, onBackground,
}) {
    const [phase, setPhase] = useState('confirm');
    const [reason, setReason] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [canTryAgain, setCanTryAgain] = useState(true);
    // Bumped by "keep running in background": the run it left behind is no longer alive.
    const runRef = useRef(0);

    useEffect(() => {
        if (open) { setPhase('confirm'); setResult(null); setError(null); setReason(''); setCanTryAgain(true); }
    }, [open]);

    const execute = async (sel) => {
        const token = ++runRef.current;
        const isAlive = () => runRef.current === token;
        setPhase('running');
        setError(null);
        try {
            const res = await run(sel, { reason: reason.trim() || undefined, isAlive });
            if (!isAlive()) return;
            setResult(res);
            setPhase('done');
            onDone?.(res);
            if (res && res.failed === 0) onClose?.();
        } catch (err) {
            if (!isAlive()) return;
            if (lmErrorCode(err) === 'selection_changed' && onSelectionChanged) {
                onSelectionChanged();
                onClose?.();
                return;
            }
            setError(lmError(err, 'The action failed. Nothing was changed for rows that did not report success.'));
            setCanTryAgain(!err?.stillRunning);
            // Polling gave up but the run goes on: let the caller refresh, as a background close does.
            if (err?.stillRunning) onBackground?.();
            setPhase('error');
        }
    };
    const keepInBackground = () => { runRef.current += 1; onBackground?.(); onClose?.(); };

    const failedIds = (result?.results || []).filter((r) => !r.ok).map((r) => r.row_id);

    return (
        <Dialog open={open} onOpenChange={(v) => {
            if (v) return;
            if (phase !== 'running') onClose?.();
            else if (background) keepInBackground();
        }}>
            <DialogContent className="sm:max-w-[560px]" data-testid="lm-bulk-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">{title}</DialogTitle>
                    {description && <DialogDescription className="text-[13px] text-muted-foreground">{description}</DialogDescription>}
                </DialogHeader>

                {phase === 'confirm' && (
                    <div className="space-y-3">
                        {count != null && (
                            <p className="text-[13px] text-foreground">
                                {count} record{count === 1 ? '' : 's'} selected.
                            </p>
                        )}
                        {extra}
                        {askReason && (
                            <label className="block">
                                <span className="text-[12.5px] font-medium text-foreground">Reason</span>
                                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={reasonPlaceholder}
                                       aria-label="Reason" required={requireReason}
                                       maxLength={reasonMaxLength}
                                       className="mt-1 w-full h-[38px] px-3 border border-input rounded-[9px] bg-card text-foreground text-[13px] outline-none placeholder:text-muted-foreground focus:border-primary" />
                            </label>
                        )}
                        <div className="flex justify-end gap-2 pt-1">
                            <GhostButton onClick={onClose}>Cancel</GhostButton>
                            <PrimaryButton tone={tone} onClick={() => execute(selection)} testId="lm-bulk-confirm"
                                           disabled={confirmDisabled || (requireReason && !reason.trim())}
                                           title={confirmDisabled ? confirmHint : undefined}>{confirmLabel}</PrimaryButton>
                        </div>
                    </div>
                )}

                {phase === 'running' && (
                    <div className="py-6 text-center" aria-live="polite">
                        <Loader2 className="w-5 h-5 mx-auto animate-spin text-muted-foreground" aria-hidden="true" />
                        <p className="mt-2 text-[13px] text-ink-2">
                            {count != null ? `Working on ${count} record${count === 1 ? '' : 's'}…` : 'Working…'}
                        </p>
                        {background && (
                            <div className="mt-4 space-y-2">
                                <p className="text-[12.5px] text-muted-foreground">It keeps running on the server if you close this, and finishes on its own.</p>
                                <GhostButton onClick={keepInBackground} testId="lm-bulk-background">Close — keep running in background</GhostButton>
                            </div>
                        )}
                    </div>
                )}

                {phase === 'error' && (
                    <div className="space-y-3" role="alert">
                        <p className="text-[13px] text-red-800 dark:text-bad">{error}</p>
                        <div className="flex justify-end gap-2">
                            <GhostButton onClick={onClose}>Close</GhostButton>
                            {canTryAgain && <PrimaryButton tone={tone} onClick={() => execute(selection)}>Try again</PrimaryButton>}
                        </div>
                    </div>
                )}

                {phase === 'done' && result && (
                    <div className="space-y-3" aria-live="polite" data-testid="lm-bulk-outcome">
                        <div className="flex gap-4 text-[13px]">
                            <span className="inline-flex items-center gap-1.5 text-emerald-800 dark:text-good"><CheckCircle2 className="w-4 h-4" />{result.succeeded} succeeded</span>
                            <span className="inline-flex items-center gap-1.5 text-red-800 dark:text-bad"><XCircle className="w-4 h-4" />{result.failed} failed</span>
                        </div>
                        {failedIds.length > 0 && (
                            <ul className="max-h-[240px] overflow-y-auto border border-border rounded-[10px] divide-y divide-border/60">
                                {(result.results || []).filter((r) => !r.ok).map((r) => (
                                    <li key={r.row_id} className="px-3 py-2 text-[12.5px]">
                                        <div className="font-medium text-foreground">{labelFor(r.row_id)}</div>
                                        <div className="text-muted-foreground">
                                            {r.error?.codes?.length ? r.error.codes.map(blockLabel).join(', ') : (r.error?.message || blockLabel(r.error?.code))}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <div className="flex justify-end gap-2">
                            <GhostButton onClick={onClose}>Close</GhostButton>
                            {failedIds.length > 0 && retryable(failedIds) && (
                                <PrimaryButton tone={tone} onClick={() => execute({ row_ids: failedIds })} testId="lm-bulk-retry">
                                    Retry {failedIds.length} failed
                                </PrimaryButton>
                            )}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export function OwnerSelect({ owners, value, onChange, includeUnassign = false, id = 'lm-owner-select',
                             label = 'Owner', placeholder = 'Choose an owner…' }) {
    return (
        <label className="block" htmlFor={id}>
            <span className="text-[12.5px] font-medium text-foreground">{label}</span>
            <select id={id} value={value} onChange={(e) => onChange(e.target.value)}
                    className="mt-1 w-full h-[38px] px-2.5 border border-input rounded-[9px] bg-card text-[13px] text-foreground">
                <option value="">{placeholder}</option>
                {includeUnassign && <option value="__unassign__">Unassigned</option>}
                {(owners || []).map((o) => <option key={o.user_id} value={o.user_id}>{o.name}</option>)}
            </select>
        </label>
    );
}
